/**
 * Labelled select.
 *
 * Replaces the monolith's `selectStyle(c)` helper, which was spread onto ~15 bare
 * <select> elements that had no associated label. Styles are the same values.
 */
import { useId } from 'react';
import type { CSSProperties } from 'react';
import { c, DISABLED_OPACITY } from '../../styles/theme';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  /** Strings are used as both value and label, matching most call sites. */
  options: readonly (string | SelectOption)[];
  /** Leading blank option, e.g. "Select a reason". */
  placeholder?: string;
  /**
   * Greys the control out and takes it out of the tab order.
   *
   * Added for the Studio's create-a-project form, where the song picker is disabled by
   * the mode above it rather than unmounted — see NewProjectForm for why. The fade is the
   * shared DISABLED_OPACITY, so a disabled select matches a disabled Button; a select
   * still has to be readable when it is off, because it shows what is currently chosen.
   */
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
  marginBottom?: number;
  style?: CSSProperties;
}

export default function Select({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  id,
  ariaLabel,
  marginBottom,
  style,
}: SelectProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;

  return (
    <div style={{ marginBottom }}>
      {label && (
        <label
          htmlFor={fieldId}
          style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}
        >
          {label}
        </label>
      )}
      <select
        id={fieldId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label ? undefined : ariaLabel}
        disabled={disabled}
        style={{
          width: '100%',
          background: c.surface2,
          border: `1px solid ${c.border}`,
          borderRadius: 8,
          fontSize: 13,
          padding: '9px 10px',
          color: c.text1,
          boxSizing: 'border-box',
          opacity: disabled ? DISABLED_OPACITY : 1,
          cursor: disabled ? 'not-allowed' : undefined,
          ...style,
        }}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => {
          const opt = typeof o === 'string' ? { value: o, label: o } : o;
          return (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          );
        })}
      </select>
    </div>
  );
}
